import React, { useState, useEffect, useRef, useMemo } from 'react';
import { dailymed, admin } from '../../services/api';
import '../../styles/DailyMedSearchModal.css';

function DailyMedSearchModal({ show, onClose, onBulkAdd }) {
  const [activeTab, setActiveTab] = useState('name');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [bulkProgress, setBulkProgress] = useState({ active: false, current: 0, total: 0, stage: '' });
  const [processingItems, setProcessingItems] = useState([]);
  const [sortOption, setSortOption] = useState('name_asc');
  
  // Search states
  const [nameQuery, setNameQuery] = useState('');
  const [drugClasses, setDrugClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [recentDays, setRecentDays] = useState(30);
  
  // Results
  const [medications, setMedications] = useState([]);
  const [selectedMeds, setSelectedMeds] = useState(new Set());
  const [page, setPage] = useState(1);
  const pageSize = 50;
  
  // Debounce for name search
  const searchDebounceRef = useRef(null);
  const processingPollRef = useRef(null);

  const HELP_PLACEHOLDER = "Try 'aspirin', 'blood pressure', or 'diabetes'";

  const getSearchTips = (tab) => {
    switch (tab) {
      case 'name':
        return "Search by medication name or a broad term (e.g., 'statin'). Use at least 2 characters.";
      case 'class':
        return "Pick a drug class, then load medications. If results are too narrow, try Name search instead.";
      case 'recent':
        return "Recently updated labels can be large. Try 7 days first for faster results.";
      case 'top':
        return "Top Prescribed is preloaded. Adding will look up real SetIDs when needed.";
      default:
        return "";
    }
  };

  const getFriendlyError = (err, context = 'search') => {
    const status = err?.response?.status;
    const message = (err?.response?.data?.detail || err?.message || '').toString();

    if (message.toLowerCase().includes('timeout') || err?.code === 'ECONNABORTED') {
      return 'Search taking too long. Try a more specific query.';
    }

    if (status === 503 || status === 502 || status === 504) {
      return 'DailyMed API temporarily unavailable. Try again in a moment.';
    }

    // Network-ish errors
    if (
      message.toLowerCase().includes('network') ||
      message.toLowerCase().includes('failed to fetch') ||
      message.toLowerCase().includes('offline')
    ) {
      return 'Search failed. Check your internet connection.';
    }

    if (context === 'no_results') {
      return 'No results found. Try a different search term.';
    }

    return err?.response?.data?.detail || 'Search failed. Check your internet connection.';
  };

  const highlightText = (text, query) => {
    if (!query || !text) return text;
    const q = query.trim();
    if (q.length < 2) return text;

    // Escape regex special chars
    const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`(${escaped})`, 'ig');
    const parts = String(text).split(re);
    return parts.map((part, idx) => {
      if (part.match(re)) {
        return (
          <mark key={idx} className="search-highlight">{part}</mark>
        );
      }
      return <React.Fragment key={idx}>{part}</React.Fragment>;
    });
  };

  const formatProcessingStatus = (status) => {
    const statusMap = {
      queued: 'Queued',
      fetching: 'Fetching DailyMed',
      simplifying: 'Simplifying',
      validating: 'Validating',
      review_ready: 'Ready for Review',
      simplify_failed: 'Failed',
    };
    return statusMap[status] || status || 'Queued';
  };

  const pollProcessingStatuses = React.useCallback(async () => {
    if (processingItems.length === 0) return;

    try {
      const nextItems = await Promise.all(
        processingItems.map(async (item) => {
          if (!item.medicationId || ['review_ready', 'simplify_failed'].includes(item.status)) {
            return item;
          }

          try {
            const response = await admin.getMedicationProcessingStatus(item.medicationId);
            const data = response.data || {};
            return {
              ...item,
              status: data.status || item.status,
              progress: data.progress ?? item.progress,
              medicationName: data.medication_name || item.medicationName,
              metadata: data.metadata || item.metadata,
            };
          } catch (err) {
            return {
              ...item,
              pollError: err.response?.data?.detail || err.message,
            };
          }
        })
      );

      setProcessingItems(nextItems);
    } catch (err) {
      console.error('Processing status poll failed:', err);
    }
  }, [processingItems]);

  // Load drug classes on mount
  useEffect(() => {
    if (show) {
      loadDrugClasses();
    }
  }, [show]);

  useEffect(() => {
    if (!show || processingItems.length === 0) {
      if (processingPollRef.current) {
        clearInterval(processingPollRef.current);
        processingPollRef.current = null;
      }
      return;
    }

    const hasActive = processingItems.some(item => !['review_ready', 'simplify_failed'].includes(item.status));
    if (!hasActive) {
      if (processingPollRef.current) {
        clearInterval(processingPollRef.current);
        processingPollRef.current = null;
      }
      return;
    }

    pollProcessingStatuses();
    processingPollRef.current = setInterval(pollProcessingStatuses, 3000);

    return () => {
      if (processingPollRef.current) {
        clearInterval(processingPollRef.current);
        processingPollRef.current = null;
      }
    };
  }, [show, processingItems, pollProcessingStatuses]);

  // Auto-search on tab change for top prescribed
  useEffect(() => {
    if (show && activeTab === 'top') {
      searchTopPrescribed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, activeTab]);

  const loadDrugClasses = async () => {
    try {
      const response = await dailymed.getDrugClasses();
      setDrugClasses(response.data.drug_classes || []);
    } catch (err) {
      console.error('Failed to load drug classes:', err);
    }
  };

  // TAB 1: Drug Name Search
  const handleNameSearch = React.useCallback(async () => {
    if (!nameQuery || nameQuery.length < 2) {
      setError('Please enter at least 2 characters');
      return;
    }

    setLoading(true);
    setLoadingMessage('Searching...');
    setError(null);

    try {
      const response = await dailymed.search(nameQuery, 5000);
      const meds = response.data.medications || [];
      setMedications(meds);
      setPage(1);
      setSelectedMeds(new Set());
      if (meds.length === 0) setError(getFriendlyError(null, 'no_results'));
    } catch (err) {
      console.error('Search failed:', err);
      setError(getFriendlyError(err, 'search'));
      setMedications([]);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }, [nameQuery]);

  // Debounce name search
  useEffect(() => {
    if (activeTab === 'name' && nameQuery.length >= 2) {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      
      const timeout = setTimeout(() => {
        handleNameSearch();
      }, 500);
      
      searchDebounceRef.current = timeout;

      return () => {
        if (searchDebounceRef.current) {
          clearTimeout(searchDebounceRef.current);
        }
      };
    }
  }, [nameQuery, activeTab, handleNameSearch]);

  // TAB 2: Drug Class Search
  const handleClassSearch = async () => {
    console.log('🎯 handleClassSearch called');
    console.log('🔍 Selected Class State:', selectedClass);
    
    if (!selectedClass) {
      console.warn('⚠️ No drug class selected!');
      setError('Please select a drug class');
      return;
    }

    console.log('🚀 Starting drug class search for:', selectedClass);
    setLoading(true);
    setLoadingMessage('Loading medications...');
    setError(null);

    try {
      console.log('🔍 Drug Class Search - Selected class:', selectedClass);
      console.log('📡 Making API call to: /api/dailymed/drug-classes/' + encodeURIComponent(selectedClass) + '/medications?limit=2000');
      
      const response = await dailymed.getMedicationsByClass(selectedClass, 5000);
      
      console.log('✅ Drug Class Search - Response:', response.data);
      console.log('📊 Drug Class Search - Medications count:', response.data.medications?.length);
      console.log('🏥 Drug Class Search - First 3 medications:', response.data.medications?.slice(0, 3));
      
      const meds = response.data.medications || [];
      setMedications(meds);
      setPage(1);
      setSelectedMeds(new Set());
      if (meds.length === 0) setError(getFriendlyError(null, 'no_results'));
      
      console.log('✅ State updated successfully');
    } catch (err) {
      console.error('❌ Class search failed:', err);
      console.error('❌ Error response:', err.response);
      console.error('❌ Error data:', err.response?.data);
      setError(getFriendlyError(err, 'search'));
      setMedications([]);
    } finally {
      setLoading(false);
      setLoadingMessage('');
      console.log('🏁 Drug class search completed');
    }
  };

  // TAB 3: Recently Updated
  const handleRecentSearch = async () => {
    setLoading(true);
    setLoadingMessage('Loading recent updates...');
    setError(null);

    try {
      const response = await dailymed.getRecent(recentDays, 5000);
      const meds = response.data.medications || [];
      setMedications(meds);
      setPage(1);
      setSelectedMeds(new Set());
      if (meds.length === 0) setError(getFriendlyError(null, 'no_results'));
    } catch (err) {
      console.error('Recent search failed:', err);
      setError(getFriendlyError(err, 'search'));
      setMedications([]);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // TAB 4: Top Prescribed
  const searchTopPrescribed = async () => {
    setLoading(true);
    setLoadingMessage('Loading top prescribed...');
    setError(null);

    try {
      const response = await dailymed.getTopPrescribed(2025, 50);
      const topMeds = response.data.medications || [];
      
      // Format for display
      const formattedMeds = topMeds.map(med => ({
        setid: `TOP-${med.rank}`, // Placeholder SetID
        medication_name: med.name,
        drug_class: med.drug_class,
        category: med.category,
        rank: med.rank
      }));
      
      setMedications(formattedMeds);
      setPage(1);
      setSelectedMeds(new Set());
      if (formattedMeds.length === 0) setError(getFriendlyError(null, 'no_results'));
    } catch (err) {
      console.error('Top prescribed search failed:', err);
      setError(getFriendlyError(err, 'search'));
      setMedications([]);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // Selection handlers
  const handleToggleSelect = (setid) => {
    const newSelected = new Set(selectedMeds);
    if (newSelected.has(setid)) {
      newSelected.delete(setid);
    } else {
      newSelected.add(setid);
    }
    setSelectedMeds(newSelected);
  };

  const handleClearSelection = () => {
    setSelectedMeds(new Set());
  };

  const handleSelectAll = () => {
    const currentPageMeds = getCurrentPageMedications();
    const newSelected = new Set(selectedMeds);
    currentPageMeds.forEach(med => newSelected.add(med.setid));
    setSelectedMeds(newSelected);
  };

  const handleDeselectAll = () => {
    const currentPageMeds = getCurrentPageMedications();
    const newSelected = new Set(selectedMeds);
    currentPageMeds.forEach(med => newSelected.delete(med.setid));
    setSelectedMeds(newSelected);
  };

  const sortedMedications = useMemo(() => {
    const meds = [...medications];

    switch (sortOption) {
      case 'name_asc':
        meds.sort((a, b) => (a.medication_name || '').localeCompare(b.medication_name || ''));
        break;
      case 'date_desc':
        meds.sort((a, b) => {
          const ad = a.published_date ? new Date(a.published_date).getTime() : 0;
          const bd = b.published_date ? new Date(b.published_date).getTime() : 0;
          return bd - ad;
        });
        break;
      case 'class_asc':
        meds.sort((a, b) => (a.drug_class || '').localeCompare(b.drug_class || ''));
        break;
      default:
        break;
    }

    return meds;
  }, [medications, sortOption]);

  // Pagination
  const getCurrentPageMedications = () => {
    const startIdx = (page - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    return sortedMedications.slice(startIdx, endIdx);
  };

  const totalPages = Math.ceil(sortedMedications.length / pageSize);

  // Auto-categorization logic
  const assignCategory = (med) => {
    const drugClass = med.drug_class?.toLowerCase() || '';
    const medName = med.medication_name?.toLowerCase() || '';
    const category = med.category?.toLowerCase() || '';
    
    // Specialty keywords
    const specialtyKeywords = ['oncology', 'cancer', 'chemotherapy', 'immunotherapy', 
      'biologic', 'monoclonal', 'inhibitor', 'rare disease', 'specialty'];
    if (specialtyKeywords.some(keyword => drugClass.includes(keyword) || category.includes('specialty'))) {
      return 'Specialty';
    }
    
    // High-volume (common chronic meds)
    const highVolumeKeywords = ['metformin', 'statin', 'insulin', 'blood pressure', 
      'anticoagulant', 'antidepressant', 'antidiabetic'];
    if (highVolumeKeywords.some(keyword => medName.includes(keyword) || drugClass.includes(keyword)) ||
        category.includes('high-volume')) {
      return 'High-Volume';
    }
    
    // Generic
    if (med.marketing_category?.toLowerCase().includes('generic')) {
      return 'Generic';
    }
    
    // New Brand (approved in last 2 years)
    const publishedDate = med.published_date ? new Date(med.published_date) : null;
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
    
    if (publishedDate && publishedDate > twoYearsAgo && 
        med.marketing_category?.toLowerCase().includes('nda')) {
      return 'New Brand';
    }
    
    // OTC
    if (med.product_type?.toLowerCase().includes('otc')) {
      return 'OTC';
    }
    
    // Default
    return 'Generic';
  };

  const assignPriority = (category) => {
    switch (category) {
      case 'Specialty':
        return 'HIGH';
      case 'High-Volume':
        return 'HIGH';
      case 'New Brand':
        return 'MEDIUM-HIGH';
      case 'Generic':
        return 'MEDIUM';
      case 'OTC':
        return 'LOW';
      default:
        return 'MEDIUM';
    }
  };

  // Bulk add to queue
  const handleBulkAdd = async () => {
    if (selectedMeds.size === 0) {
      alert('Please select at least one medication');
      return;
    }

    const selectedMedications = sortedMedications.filter(med => selectedMeds.has(med.setid));

    setLoading(true);
    setLoadingMessage('Adding to queue...');
    setBulkProgress({ active: true, current: 0, total: selectedMedications.length, stage: 'Starting...' });
    setProcessingItems([]);
    setError(null);

    const results = {
      added: [],
      errors: [],
      queued: []
    };

    try {
      // Process each selected medication
      for (let i = 0; i < selectedMedications.length; i++) {
        const med = selectedMedications[i];
        setBulkProgress({
          active: true,
          current: i + 1,
          total: selectedMedications.length,
          stage: `Processing ${i + 1} of ${selectedMedications.length}`
        });

        // Skip top prescribed placeholders without real SetIDs
        if (med.setid.startsWith('TOP-')) {
          setBulkProgress(p => ({ ...p, stage: `Looking up SetID for ${med.medication_name}...` }));
          // For top prescribed, we need to search DailyMed for the real SetID
          try {
            const searchResponse = await dailymed.search(med.medication_name, 1);
            if (searchResponse.data.medications && searchResponse.data.medications.length > 0) {
              const foundMed = searchResponse.data.medications[0];
              med.setid = foundMed.setid;
              med.published_date = foundMed.published_date;
              med.marketing_category = foundMed.marketing_category;
            } else {
              results.errors.push(`${med.medication_name}: Could not find SetID in DailyMed`);
              continue;
            }
          } catch (err) {
            results.errors.push(`${med.medication_name}: Failed to lookup SetID`);
            continue;
          }
        }

        // Auto-assign category (priority handled downstream if needed)
        const category = assignCategory(med);
        assignPriority(category);

        // Add directly to pipeline (queues background processing)
        try {
          setBulkProgress(p => ({ ...p, stage: `Adding ${med.medication_name}...` }));
          const response = await admin.addFromDailyMed(med.setid);

          if (response.data && response.data.success === false) {
            results.errors.push({
              name: med.medication_name,
              reason: response.data.processing_result?.error || response.data.message || 'Processing failed'
            });
          } else {
            const medicationId = response.data?.medication_id;
            const queuedItem = {
              medicationId,
              medicationName: med.medication_name,
              status: response.data?.status || 'queued',
              progress: 0,
              action: response.data?.action || 'created',
            };
            results.queued.push(queuedItem);
            if (response.data && response.data.action === 'updated') {
              results.added.push(`${med.medication_name} (updated)`);
            } else {
              results.added.push(med.medication_name);
            }
          }
        } catch (err) {
          console.error('Failed to add:', err);
          results.errors.push({
            name: med.medication_name,
            reason: err.response?.data?.detail || 'Failed to add'
          });
        }
      }

      if (results.queued.length > 0) {
        setProcessingItems(results.queued);
      }

      // Show results summary
      const message = `
        ✅ Added/Updated: ${results.added.length}
        ⏳ Processing: ${results.queued.length}
        ❌ Errors: ${results.errors.length}
        
        ${results.errors.length > 0 ? '\nErrors:\n' + results.errors.slice(0, 5).map(e =>
          typeof e === 'string' ? e : `${e.name}: ${e.reason}`
        ).join('\n') : ''}
      `.trim();

      alert(message);

      if (results.added.length > 0) {
        // Call parent callback
        if (onBulkAdd) {
          onBulkAdd(results);
        }

        // Reset selections
        setSelectedMeds(new Set());
      }

    } catch (err) {
      console.error('Bulk add failed:', err);
      setError('Bulk add failed. ' + getFriendlyError(err, 'search'));
    } finally {
      setLoading(false);
      setLoadingMessage('');
      setBulkProgress({ active: false, current: 0, total: 0, stage: '' });
    }
  };

  if (!show) return null;

  const currentPageMeds = getCurrentPageMedications();

  return (
    <div className="modal-overlay" onClick={loading ? undefined : onClose}>
      <div className="modal-content dailymed-search-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🔍 Search DailyMed</h2>
          <button className="close-button" onClick={onClose} disabled={loading} title={loading ? 'Please wait…' : 'Close'}>×</button>
        </div>

        <div className="search-tabs">
          <button 
            className={`tab-button ${activeTab === 'name' ? 'active' : ''}`}
            onClick={() => setActiveTab('name')}
            disabled={loading}
          >
            Drug Name
          </button>
          <button 
            className={`tab-button ${activeTab === 'class' ? 'active' : ''}`}
            onClick={() => setActiveTab('class')}
            disabled={loading}
          >
            Drug Class
          </button>
          <button 
            className={`tab-button ${activeTab === 'recent' ? 'active' : ''}`}
            onClick={() => setActiveTab('recent')}
            disabled={loading}
          >
            Recently Updated
          </button>
          <button 
            className={`tab-button ${activeTab === 'top' ? 'active' : ''}`}
            onClick={() => setActiveTab('top')}
            disabled={loading}
          >
            Top Prescribed
          </button>
        </div>

        <div className="search-form">
          {/* TAB 1: Drug Name */}
          {activeTab === 'name' && (
            <div className="search-input-group" style={{ pointerEvents: 'auto', position: 'relative' }}>
              <input
                type="text"
                placeholder={HELP_PLACEHOLDER}
                value={nameQuery}
                onChange={(e) => setNameQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleNameSearch()}
                autoFocus
                disabled={false}
                readOnly={false}
                style={{ pointerEvents: 'auto', opacity: 1 }}
                aria-label="Search by medication name"
              />
              <button 
                className="btn-primary" 
                onClick={handleNameSearch}
                disabled={loading || nameQuery.length < 2}
                title={getSearchTips('name')}
              >
                {loading ? 'Searching…' : 'Search'}
              </button>
              <button
                className="btn-secondary btn-sm"
                type="button"
                onClick={() => alert(getSearchTips('name'))}
                disabled={loading}
                title="Search tips"
              >
                ?
              </button>
            </div>
          )}

          {/* TAB 2: Drug Class */}
          {activeTab === 'class' && (
            <div className="search-input-group">
              <select
                value={selectedClass}
                onChange={(e) => {
                  console.log('📋 Drug class selected:', e.target.value);
                  setSelectedClass(e.target.value);
                }}
                disabled={loading}
                aria-label="Select drug class"
                title={getSearchTips('class')}
              >
                <option value="">Select a drug class...</option>
                {drugClasses.map(cls => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
              <button 
                className="btn-primary" 
                onClick={() => {
                  console.log('🔘 Load Medications button clicked');
                  console.log('🔍 Current selectedClass:', selectedClass);
                  handleClassSearch();
                }}
                disabled={loading || !selectedClass}
                title={getSearchTips('class')}
              >
                {loading ? 'Loading…' : 'Load Medications'}
              </button>
              <button
                className="btn-secondary btn-sm"
                type="button"
                onClick={() => alert(getSearchTips('class'))}
                disabled={loading}
                title="Search tips"
              >
                ?
              </button>
            </div>
          )}

          {/* TAB 3: Recently Updated */}
          {activeTab === 'recent' && (
            <div className="search-input-group">
              <div className="date-range-buttons">
                <button
                  className={`btn-filter ${recentDays === 7 ? 'active' : ''}`}
                  onClick={() => setRecentDays(7)}
                  disabled={loading}
                >
                  Last 7 Days
                </button>
                <button
                  className={`btn-filter ${recentDays === 30 ? 'active' : ''}`}
                  onClick={() => setRecentDays(30)}
                  disabled={loading}
                >
                  Last 30 Days
                </button>
                <button
                  className={`btn-filter ${recentDays === 90 ? 'active' : ''}`}
                  onClick={() => setRecentDays(90)}
                  disabled={loading}
                >
                  Last 90 Days
                </button>
              </div>
              <button 
                className="btn-primary" 
                onClick={handleRecentSearch}
                disabled={loading}
                title={getSearchTips('recent')}
              >
                {loading ? 'Loading…' : 'Load Recent'}
              </button>
              <button
                className="btn-secondary btn-sm"
                type="button"
                onClick={() => alert(getSearchTips('recent'))}
                disabled={loading}
                title="Search tips"
              >
                ?
              </button>
            </div>
          )}

          {/* TAB 4: Top Prescribed */}
          {activeTab === 'top' && (
            <div className="top-prescribed-info">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                <div>
                  <p>📊 Top 50 medications by gross sales (2025)</p>
                  <p className="info-note">These medications are pre-loaded and ready to add to your queue.</p>
                </div>
                <button
                  className="btn-secondary btn-sm"
                  type="button"
                  onClick={() => alert(getSearchTips('top'))}
                  disabled={loading}
                  title="Search tips"
                >
                  ?
                </button>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="error-message">
            ⚠️ {error}
          </div>
        )}

        {loading && (
          <div className="loading-state" aria-live="polite">
            <div className="spinner"></div>
            <p>{loadingMessage || 'Searching…'} {bulkProgress.active ? `(${bulkProgress.current}/${bulkProgress.total})` : ''}</p>
            {bulkProgress.active && (
              <div className="bulk-progress" style={{ width: '100%', marginTop: 8 }}>
                <div style={{ fontSize: 12, opacity: 0.85 }}>{bulkProgress.stage}</div>
                <div style={{ height: 8, background: 'rgba(0,0,0,0.08)', borderRadius: 999, overflow: 'hidden', marginTop: 6 }}>
                  <div
                    style={{
                      height: '100%',
                      width: bulkProgress.total ? `${Math.round((bulkProgress.current / bulkProgress.total) * 100)}%` : '0%',
                      background: 'linear-gradient(90deg, rgba(76,175,80,0.9), rgba(33,150,243,0.9))',
                      transition: 'width 200ms ease'
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {processingItems.length > 0 && (
          <div className="processing-status-panel" aria-live="polite" style={{ marginBottom: 16, padding: 12, border: '1px solid #d8e2f0', borderRadius: 8, background: '#f8fbff' }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Processing status</div>
            <div style={{ display: 'grid', gap: 8 }}>
              {processingItems.map((item, idx) => (
                <div key={`${item.medicationId || item.medicationName}-${idx}`} style={{ padding: 10, borderRadius: 6, background: 'white', border: '1px solid #e6edf5' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                    <strong>{item.medicationName}</strong>
                    <span style={{ fontSize: 12, fontWeight: 600, color: item.status === 'review_ready' ? '#2e7d32' : item.status === 'simplify_failed' ? '#c62828' : '#1565c0' }}>
                      {formatProcessingStatus(item.status)}
                    </span>
                  </div>
                  <div style={{ height: 8, background: 'rgba(0,0,0,0.08)', borderRadius: 999, overflow: 'hidden', marginTop: 8 }}>
                    <div
                      style={{
                        height: '100%',
                        width: `${item.progress ?? 0}%`,
                        background: item.status === 'review_ready'
                          ? 'linear-gradient(90deg, #43a047, #66bb6a)'
                          : item.status === 'simplify_failed'
                            ? 'linear-gradient(90deg, #e53935, #ef5350)'
                            : 'linear-gradient(90deg, #1e88e5, #42a5f5)',
                        transition: 'width 250ms ease'
                      }}
                    />
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
                    {item.status === 'review_ready' && 'Ready for pharmacist review.'}
                    {item.status === 'simplify_failed' && `Processing failed${item.metadata?.simplify_error ? `: ${item.metadata.simplify_error}` : ''}`}
                    {!['review_ready', 'simplify_failed'].includes(item.status) && `${item.progress ?? 0}% complete`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && sortedMedications.length > 0 && (
          <>
            <div className="results-header">
              <div className="results-count">
                Found {sortedMedications.length} medications · {selectedMeds.size} selected
              </div>

              <div className="selection-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, opacity: 0.9 }}>
                  Sort:
                  <select
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                    className="sort-select"
                    disabled={loading}
                    aria-label="Sort results"
                  >
                    <option value="name_asc">Name (A–Z)</option>
                    <option value="date_desc">Date (newest first)</option>
                    <option value="class_asc">Drug Class</option>
                  </select>
                </label>

                {selectedMeds.size > 0 && (
                  <button
                    className="btn-secondary btn-sm"
                    onClick={handleClearSelection}
                    disabled={loading}
                    title="Clear all selected medications"
                  >
                    Clear selection
                  </button>
                )}

                <button 
                  className="btn-secondary btn-sm" 
                  onClick={handleSelectAll}
                  disabled={loading || currentPageMeds.every(med => selectedMeds.has(med.setid))}
                >
                  Select All (Page)
                </button>
                <button 
                  className="btn-secondary btn-sm" 
                  onClick={handleDeselectAll}
                  disabled={loading || currentPageMeds.every(med => !selectedMeds.has(med.setid))}
                >
                  Deselect All (Page)
                </button>
              </div>
            </div>

            <div className="results-table-container">
              <table className="results-table">
                <thead>
                  <tr>
                    <th width="40">
                      <input
                        type="checkbox"
                        checked={currentPageMeds.length > 0 && currentPageMeds.every(med => selectedMeds.has(med.setid))}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleSelectAll();
                          } else {
                            handleDeselectAll();
                          }
                        }}
                        disabled={loading}
                        aria-label="Select all on current page"
                      />
                    </th>
                    <th>Medication Name</th>
                    <th>Drug Class</th>
                    {activeTab === 'top' && <th>Rank</th>}
                    <th>Category</th>
                    <th>Priority</th>
                    {activeTab !== 'top' && <th>SetID</th>}
                  </tr>
                </thead>
                <tbody>
                  {currentPageMeds.map(med => {
                    const category = assignCategory(med);
                    const priority = assignPriority(category);
                    
                    return (
                      <tr
                        key={med.setid}
                        className={selectedMeds.has(med.setid) ? 'selected' : ''}
                        onClick={() => !loading && handleToggleSelect(med.setid)}
                        style={{ cursor: loading ? 'not-allowed' : 'pointer', transition: 'background-color 150ms ease' }}
                        title={loading ? 'Please wait…' : 'Click to select'}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedMeds.has(med.setid)}
                            onChange={() => handleToggleSelect(med.setid)}
                            disabled={loading}
                            aria-label={`Select ${med.medication_name}`}
                          />
                        </td>
                        <td className="med-name">
                          <strong>{activeTab === 'name' ? highlightText(med.medication_name, nameQuery) : med.medication_name}</strong>
                          {med.labeler && (
                            <div className="med-labeler">{med.labeler}</div>
                          )}
                        </td>
                        <td>{med.drug_class || '-'}</td>
                        {activeTab === 'top' && <td>#{med.rank}</td>}
                        <td>
                          <span className={`category-badge category-${category.toLowerCase().replace(' ', '-')}`}>
                            {category}
                          </span>
                        </td>
                        <td>
                          <span className={`priority-badge priority-${priority.toLowerCase()}`}>
                            {priority}
                          </span>
                        </td>
                        {activeTab !== 'top' && (
                          <td className="setid-cell">
                            <code>{med.setid?.substring(0, 12)}...</code>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="results-pagination">
                <button
                  className="btn-secondary btn-sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  ← Previous
                </button>
                <span className="page-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  className="btn-secondary btn-sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}

        {!loading && sortedMedications.length === 0 && !error && activeTab !== 'top' && (
          <div className="no-results" aria-live="polite" style={{ padding: '18px 12px' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ fontSize: 22, lineHeight: 1 }}>🔎</div>
              <div>
                <p style={{ margin: 0, fontWeight: 600 }}>No medications found</p>
                <p style={{ margin: '6px 0 0 0', opacity: 0.85 }}>Try a different search term.</p>
                <ul style={{ margin: '10px 0 0 18px', opacity: 0.9 }}>
                  <li>Try searching for <strong>"aspirin"</strong> or <strong>"statin"</strong></li>
                  <li>Use broader terms for more results</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button 
            className="btn-primary"
            onClick={handleBulkAdd}
            disabled={selectedMeds.size === 0 || loading}
          >
            {loading ? 'Adding...' : `Add ${selectedMeds.size} to Queue`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DailyMedSearchModal;
